import { useRef, useEffect, useState, useCallback } from 'react';
import {
  createChart,
  CandlestickSeries,
  type IChartApi,
  type ISeriesApi,
  type UTCTimestamp,
  type CandlestickData,
  ColorType,
  CrosshairMode,
  LineStyle,
} from 'lightweight-charts';
import { useBotCandles, useBotLiveCandles, type OpenPosition } from '@/hooks/use-bots';
import { formatPnl } from '@/lib/format';
import { useTheme } from '@/lib/theme';
import { getChartColors } from '@/lib/chart-colors';
import { Spinner } from '@/components/ui/spinner';
import type { OhlcCandle } from '@/types/api';

interface PendingLimitOrder {
  orderId: string;
  type: 'BUY' | 'SELL';
  limitPrice: number;
  volume: number;
}

interface LiveChartSectionProps {
  botId: string;
  symbol: string;
  isActive: boolean;
  openPositions: OpenPosition[];
  pendingLimitOrders?: PendingLimitOrder[];
  currentPrice?: number;
  /** Strategy timeframe from bot.rules.market_data.timeframe — used as default */
  strategyTimeframe?: string;
  /** Stable `on` function from the parent's useBotSocket — avoids duplicate subscribe/unsubscribe */
  on: (event: string, handler: (...args: any[]) => void) => () => void;
  /** Currency code for formatting monetary values (defaults to USD) */
  currency?: string;
}

type Timeframe = '1m' | '5m' | '15m' | '30m' | '1h' | '4h' | '1d';

const TIMEFRAMES: Timeframe[] = ['1m', '5m', '15m', '30m', '1h', '4h', '1d'];

const toEpochSec = (t: number): number =>
  t > 1e12 ? Math.floor(t / 1000) : Math.floor(t);

/**
 * Remove dead-market candles (market close noise) from the trailing end.
 * During market close, brokers still emit candles but they're just spread fluctuations
 * around the last traded price — nearly zero range and no real movement.
 *
 * Detects these by comparing each candle's range to the median active range.
 * Trims trailing streaks of dead candles (>= 6 consecutive) from the end.
 */
function trimDeadMarketCandles(candles: CandlestickData<UTCTimestamp>[]): CandlestickData<UTCTimestamp>[] {
  if (candles.length < 10) return candles;

  // Calculate range for each candle
  const ranges = candles.map(c => c.high - c.low);

  // Get median range from the first half (more likely to contain active trading)
  const firstHalf = ranges.slice(0, Math.floor(ranges.length / 2));
  const sortedRanges = [...firstHalf].sort((a, b) => a - b);
  const medianRange = sortedRanges[Math.floor(sortedRanges.length / 2)] ?? 0;
  if (medianRange <= 0) return candles;

  // A candle is "dead" if its range is < 5% of the median active range
  const deadThreshold = medianRange * 0.05;

  // Walk backward from the end to find the trailing dead streak
  let trimFrom = candles.length;
  for (let i = candles.length - 1; i >= 0; i--) {
    if ((ranges[i] ?? 0) < deadThreshold) {
      trimFrom = i;
    } else {
      break;
    }
  }

  // Only trim if there are at least 6 consecutive dead candles at the end
  const deadCount = candles.length - trimFrom;
  if (deadCount >= 6) {
    return candles.slice(0, trimFrom);
  }
  return candles;
}

/**
 * Clamp spread-spike wicks that distort the chart.
 * Uses median body size as baseline — wicks beyond 5x median body are clamped.
 */
function sanitizeWicks(candles: CandlestickData<UTCTimestamp>[]): CandlestickData<UTCTimestamp>[] {
  if (candles.length < 5) return candles;

  const bodies = candles.map(c => Math.abs(c.close - c.open)).sort((a, b) => a - b);
  const medianBody = bodies[Math.floor(bodies.length / 2)] ?? 0;
  const q1 = bodies[Math.floor(bodies.length * 0.25)] ?? 0;
  const q3 = bodies[Math.floor(bodies.length * 0.75)] ?? 0;
  const iqr = q3 - q1;
  const refPrice = candles[Math.floor(candles.length / 2)]?.close ?? 0;
  if (refPrice === 0) return candles;
  const minFloor = refPrice * 0.005;
  const maxWick = Math.max(medianBody * 15, iqr * 5, minFloor);

  return candles.map(c => {
    const bodyHigh = Math.max(c.open, c.close);
    const bodyLow = Math.min(c.open, c.close);
    return {
      ...c,
      high: Math.min(c.high, bodyHigh + maxWick),
      low: Math.max(c.low, bodyLow - maxWick),
    };
  });
}

function convertCandles(candles: OhlcCandle[]): CandlestickData<UTCTimestamp>[] {
  const map = new Map<number, OhlcCandle>();
  for (const c of candles) map.set(toEpochSec(c.time), c);
  const sorted = Array.from(map.values())
    .sort((a, b) => a.time - b.time)
    .map((c) => ({
      time: toEpochSec(c.time) as UTCTimestamp,
      open: c.open,
      high: c.high,
      low: c.low,
      close: c.close,
    }));
  return sanitizeWicks(trimDeadMarketCandles(sorted));
}

const TF_STORAGE_PREFIX = 'chart-tf:';
const RANGE_STORAGE_PREFIX = 'chart-range:';

function getInitialTimeframe(botId: string, strategyTimeframe?: string): Timeframe {
  const stored = localStorage.getItem(TF_STORAGE_PREFIX + botId);
  if (stored && TIMEFRAMES.includes(stored as Timeframe)) return stored as Timeframe;
  if (strategyTimeframe && TIMEFRAMES.includes(strategyTimeframe as Timeframe)) return strategyTimeframe as Timeframe;
  return '5m';
}

export function LiveChartSection({ botId, symbol, isActive, openPositions, pendingLimitOrders, currentPrice, strategyTimeframe, on, currency = 'USD' }: LiveChartSectionProps) {
  const [timeframe, setTimeframeState] = useState<Timeframe>(() => getInitialTimeframe(botId, strategyTimeframe));
  const [chartReady, setChartReady] = useState(false);
  const { theme } = useTheme();
  const colors = getChartColors(theme);

  const setTimeframe = (tf: Timeframe) => {
    setTimeframeState(tf);
    localStorage.setItem(TF_STORAGE_PREFIX + botId, tf);
  };
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  const priceLineRef = useRef<any>(null);
  const limitOrderLinesRef = useRef<any[]>([]);
  const positionLinesRef = useRef<any[]>([]);
  const prevDataKeyRef = useRef<string>('');
  const buildingCandleRef = useRef<{ time: number; open: number; high: number; low: number; close: number } | null>(null);
  // Median candle range from loaded data — used to reject dead-market WS candles
  const medianRangeRef = useRef(0);
  // Guards: prevent tick/candle updates before initial data is loaded
  const dataLoadedRef = useRef(false);

  // Data fetching
  const days = timeframe === '1m' ? 1 : timeframe === '5m' ? 2 : timeframe === '15m' ? 5 : timeframe === '30m' ? 7 : timeframe === '1h' ? 14 : timeframe === '4h' ? 30 : 30;
  const { data: candleData, isFetching: isFetchingCandles } = useBotCandles(botId, { timeframe, days });
  const { data: liveData } = useBotLiveCandles(botId, { timeframe, enabled: isActive });

  // Merge historical + live candles into a deduped, sorted array
  const mergedCandles: OhlcCandle[] = (() => {
    const hist = candleData?.candles ?? [];
    const live = liveData?.candles ?? [];
    const map = new Map<number, OhlcCandle>();
    for (const c of hist) map.set(toEpochSec(c.time), c);
    for (const c of live) map.set(toEpochSec(c.time), c);
    return Array.from(map.values()).sort((a, b) => a.time - b.time);
  })();

  // Recreate chart when theme changes
  useEffect(() => {
    if (!containerRef.current) return;

    // Clean up previous chart
    if (chartRef.current) {
      try {
        const range = chartRef.current.timeScale().getVisibleLogicalRange();
        if (range) {
          sessionStorage.setItem(RANGE_STORAGE_PREFIX + botId, JSON.stringify(range));
        }
      } catch { /* chart may already be disposed */ }
      chartRef.current.remove();
      chartRef.current = null;
      seriesRef.current = null;
      priceLineRef.current = null;
      positionLinesRef.current = [];
      limitOrderLinesRef.current = [];
      buildingCandleRef.current = null;
      dataLoadedRef.current = false;
      prevDataKeyRef.current = '';
    }

    const chart = createChart(containerRef.current, {
      autoSize: true,
      height: 420,
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: colors.textColor,
        fontSize: 9,
      },
      grid: {
        vertLines: { color: colors.gridColor },
        horzLines: { color: colors.gridColor },
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: {
          color: colors.crosshairColor,
          width: 1,
          style: LineStyle.Dashed,
          labelBackgroundColor: colors.crosshairLabelBg,
        },
        horzLine: {
          color: colors.crosshairColor,
          width: 1,
          style: LineStyle.Dashed,
          labelBackgroundColor: colors.crosshairLabelBg,
        },
      },
      rightPriceScale: {
        borderVisible: false,
        scaleMargins: { top: 0.05, bottom: 0.05 },
      },
      timeScale: {
        borderVisible: false,
        timeVisible: true,
        rightOffset: 12,
      },
    });

    const series = chart.addSeries(CandlestickSeries, {
      upColor: colors.upColor,
      downColor: colors.downColor,
      borderUpColor: colors.upColorBorder,
      borderDownColor: colors.downColorBorder,
      wickUpColor: colors.upColorWick,
      wickDownColor: colors.downColorWick,
      priceLineVisible: false,
      lastValueVisible: false,
    });

    chartRef.current = chart;
    seriesRef.current = series;

    return () => {
      // Save visible range before destroying so it can be restored on remount
      try {
        const range = chart.timeScale().getVisibleLogicalRange();
        if (range) {
          sessionStorage.setItem(RANGE_STORAGE_PREFIX + botId, JSON.stringify(range));
        }
      } catch { /* chart may already be disposed */ }
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
      priceLineRef.current = null;
      positionLinesRef.current = [];
      limitOrderLinesRef.current = [];
      buildingCandleRef.current = null;
      dataLoadedRef.current = false;
      prevDataKeyRef.current = '';
    };
  }, [theme]);

  // Mark chart ready once the initial fetch resolves (even if empty)
  useEffect(() => {
    if (!chartReady && !isFetchingCandles && candleData !== undefined) {
      setChartReady(true);
    }
  }, [chartReady, isFetchingCandles, candleData]);

  // Set candle data — setData() replaces all data, then scroll to recent candles
  useEffect(() => {
    if (!seriesRef.current || mergedCandles.length === 0) return;
    // Wait for historical candles before rendering — prevents showing only 1-2 giant
    // live-buffer candles while the API fetch is still in-flight.
    if (candleData === undefined) return;

    const converted = convertCandles(mergedCandles);

    const first = converted[0]?.time ?? 0;
    const last = converted[converted.length - 1]?.time ?? 0;
    const lastClose = converted[converted.length - 1]?.close ?? 0;
    const dataKey = `${timeframe}:${converted.length}:${first}:${last}:${lastClose}`;

    if (dataKey === prevDataKeyRef.current) return;

    const isNewTimeframe = !prevDataKeyRef.current.startsWith(timeframe + ':');
    prevDataKeyRef.current = dataKey;
    dataLoadedRef.current = true;
    if (!chartReady) setChartReady(true);

    // Compute median range from active candles — used to reject dead-market WS updates
    if (converted.length >= 5) {
      const ranges = converted.map(c => c.high - c.low).sort((a, b) => a - b);
      medianRangeRef.current = ranges[Math.floor(ranges.length / 2)] ?? 0;
    }

    seriesRef.current.setData(converted);

    if (isNewTimeframe && converted.length > 0) {
      // Defer range setting — the chart needs a layout pass after setData before
      // setVisibleLogicalRange works correctly (especially on remount when data
      // loads from cache in the same frame as chart creation).
      requestAnimationFrame(() => {
        if (!chartRef.current) return;

        // Restore saved range from a previous visit, otherwise fit all candles
        const savedKey = RANGE_STORAGE_PREFIX + botId;
        const saved = sessionStorage.getItem(savedKey);
        if (saved) {
          try {
            const range = JSON.parse(saved);
            chartRef.current.timeScale().setVisibleLogicalRange(range);
          } catch { /* ignore corrupt data */ }
          sessionStorage.removeItem(savedKey);
        } else {
          // Default zoom: show the most recent 55 candles regardless of timeframe
          const barsToShow = 55;
          const fromIndex = Math.max(0, converted.length - barsToShow);
          chartRef.current.timeScale().setVisibleLogicalRange({
            from: fromIndex,
            to: converted.length + 3,
          });
        }
      });
    }
  }, [mergedCandles, timeframe, candleData, liveData]);

  // Listen for WS candle updates — completed candles pushed from engine
  useEffect(() => {
    return on('candle:update', (data: any) => {
      if (!seriesRef.current || !dataLoadedRef.current || !data?.candle) return;
      if (data.timeframe && data.timeframe !== timeframe) return;

      const c = data.candle;
      const range = c.high - c.low;

      // Skip dead-market candles — range < 0.5% of median active range (very lenient)
      if (medianRangeRef.current > 0 && range < medianRangeRef.current * 0.005) return;

      // Clamp spread-spike wicks on incoming candles
      const bodyHigh = Math.max(c.open, c.close);
      const bodyLow = Math.min(c.open, c.close);
      const body = bodyHigh - bodyLow;
      const maxWick = Math.max(body * 15, bodyHigh * 0.005);
      const bar: CandlestickData<UTCTimestamp> = {
        time: toEpochSec(c.time) as UTCTimestamp,
        open: c.open,
        high: Math.min(c.high, bodyHigh + maxWick),
        low: Math.max(c.low, bodyLow - maxWick),
        close: c.close,
      };
      seriesRef.current!.update(bar);

      // Reset building candle for the new period
      buildingCandleRef.current = {
        time: toEpochSec(c.time),
        open: c.open,
        high: c.high,
        low: c.low,
        close: c.close,
      };
    });
  }, [on, timeframe]);

  // Initialize blue price line from candle data (before any ticks arrive)
  useEffect(() => {
    if (!seriesRef.current || priceLineRef.current) return;
    const price = currentPrice || mergedCandles[mergedCandles.length - 1]?.close;
    if (!price) return;

    priceLineRef.current = seriesRef.current.createPriceLine({
      price,
      color: colors.priceLineColor,
      lineWidth: 1,
      lineStyle: LineStyle.Solid,
      axisLabelVisible: true,
      title: '',
      lineVisible: true,
      axisLabelColor: colors.priceLineLabelColor,
      axisLabelTextColor: colors.priceLineLabelTextColor,
    });
  }, [currentPrice, mergedCandles.length, colors]);

  // Position entry lines
  useEffect(() => {
    if (!seriesRef.current) return;
    const series = seriesRef.current;

    for (const line of positionLinesRef.current) {
      series.removePriceLine(line);
    }
    positionLinesRef.current = [];

    for (const pos of openPositions) {
      if (!pos.open_price) continue;
      const isBuy = pos.type === 'BUY';
      const pnl = pos.floating_pnl ?? 0;
      const pnlText = `${isBuy ? 'BUY' : 'SELL'} ${pos.volume}  ${formatPnl(pnl, currency)}`;
      const color = pnl >= 0 ? colors.upColor : colors.downColor;

      const line = series.createPriceLine({
        price: pos.open_price,
        color,
        lineWidth: 1,
        lineStyle: LineStyle.Dashed,
        axisLabelVisible: true,
        title: pnlText,
        lineVisible: true,
        axisLabelColor: color,
        axisLabelTextColor: colors.priceLineLabelTextColor,
      });
      positionLinesRef.current.push(line);
    }
  }, [openPositions, symbol, colors]);

  // Pending limit order lines (gray dotted)
  useEffect(() => {
    if (!seriesRef.current) return;
    const series = seriesRef.current;

    for (const line of limitOrderLinesRef.current) {
      series.removePriceLine(line);
    }
    limitOrderLinesRef.current = [];

    const orders = pendingLimitOrders ?? [];
    for (const lo of orders) {
      if (!lo.limitPrice) continue;
      const label = `${lo.type} LIMIT ${lo.volume}`;
      const gray = '#888888';

      const line = series.createPriceLine({
        price: lo.limitPrice,
        color: gray,
        lineWidth: 1,
        lineStyle: LineStyle.Dotted,
        axisLabelVisible: true,
        title: label,
        lineVisible: true,
        axisLabelColor: gray,
        axisLabelTextColor: '#ffffff',
      });
      limitOrderLinesRef.current.push(line);
    }
  }, [pendingLimitOrders, colors]);

  // Reset building candle when server candle data changes (new candle period)
  const lastMerged = mergedCandles[mergedCandles.length - 1] as OhlcCandle | undefined;
  const lastCandleKey = lastMerged
    ? `${timeframe}:${toEpochSec(lastMerged.time)}`
    : '';
  useEffect(() => {
    if (!lastMerged) return;
    buildingCandleRef.current = {
      time: toEpochSec(lastMerged.time),
      open: lastMerged.open,
      high: lastMerged.high,
      low: lastMerged.low,
      close: lastMerged.close,
    };
  }, [lastCandleKey]);

  // Timeframe duration in seconds for candle boundary detection
  const tfSeconds: Record<string, number> = { '1m': 60, '5m': 300, '15m': 900, '30m': 1800, '1h': 3600, '4h': 14400, '1d': 86400 };

  // Shared handler for price ticks (used by both bot:tick and market:tick)
  const handlePriceTick = useCallback((price: number) => {
    if (!price || price <= 0) return;

    // Update (or create) the blue price line
    if (priceLineRef.current) {
      priceLineRef.current.applyOptions({ price });
    } else if (seriesRef.current) {
      priceLineRef.current = seriesRef.current.createPriceLine({
        price,
        color: colors.priceLineColor,
        lineWidth: 1,
        lineStyle: LineStyle.Solid,
        axisLabelVisible: true,
        title: '',
        lineVisible: true,
        axisLabelColor: colors.priceLineLabelColor,
        axisLabelTextColor: colors.priceLineLabelTextColor,
      });
    }

    // Update building candle — only after initial data has been loaded
    const bc = buildingCandleRef.current;
    if (bc && seriesRef.current && dataLoadedRef.current) {
      const periodSec = tfSeconds[timeframe] || 60;
      const nowSec = Math.floor(Date.now() / 1000);
      const currentPeriod = Math.floor(nowSec / periodSec) * periodSec;

      if (currentPeriod > bc.time) {
        // New candle period — jump to current period.
        // Any gaps between the old candle and now will be filled
        // by the server refetch (useBotLiveCandles, every 5s).
        buildingCandleRef.current = {
          time: currentPeriod,
          open: price,
          high: price,
          low: price,
          close: price,
        };
        seriesRef.current.update({
          time: currentPeriod as UTCTimestamp,
          open: price,
          high: price,
          low: price,
          close: price,
        });
      } else {
        // Same period — update existing candle
        bc.high = Math.max(bc.high, price);
        bc.low = Math.min(bc.low, price);
        bc.close = price;
        seriesRef.current.update({
          time: bc.time as UTCTimestamp,
          open: bc.open,
          high: bc.high,
          low: bc.low,
          close: bc.close,
        });
      }
    }
  }, [colors, timeframe]);

  // Real-time: bot:tick — update price line + building candle
  useEffect(() => {
    return on('bot:tick', (data: any) => {
      handlePriceTick(data.bid || data.ask);
    });
  }, [on, handlePriceTick]);

  // Market feed: always-on ticks (works even when bot is stopped)
  useEffect(() => {
    return on('market:tick', (data: any) => {
      handlePriceTick(data.bid || data.ask);
    });
  }, [on, handlePriceTick]);

  return (
    <div className="glass-sm rounded-lg p-3">
      {/* Header with timeframe selector */}
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] font-semibold text-ink-4 uppercase tracking-wider">
          Live Chart
        </p>
        <div className="flex gap-0.5">
          {TIMEFRAMES.map((tf) => (
            <button
              key={tf}
              onClick={() => setTimeframe(tf)}
              className={`text-[9px] font-bold px-2 py-1 rounded transition-all cursor-pointer ${
                timeframe === tf
                  ? 'bg-orange-bg text-orange'
                  : 'text-ink-4 hover:text-ink-2 hover:bg-surface'
              }`}
            >
              {tf.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Chart container */}
      <div className="rounded-md overflow-hidden relative">
        <div
          ref={containerRef}
          style={{ height: 420 }}
          className={`transition-opacity duration-300 ${chartReady ? 'opacity-100' : 'opacity-0'}`}
        />
        {!chartReady && (
          <div className={`absolute inset-0 flex items-center justify-center backdrop-blur-[1px] z-10 ${theme === 'dark' ? 'bg-black/40' : 'bg-white/50'}`}>
            <Spinner size="sm" />
          </div>
        )}
      </div>

      {/* Position summary below chart */}
      {(openPositions.length > 0 || (pendingLimitOrders && pendingLimitOrders.length > 0)) && (
        <div className="mt-2 flex items-center gap-3 text-[10px] text-ink-4 flex-wrap">
          {openPositions.length > 0 && (
            <span className="font-semibold">{openPositions.length} open position{openPositions.length > 1 ? 's' : ''}</span>
          )}
          {openPositions.map((pos) => {
            const isBuy = pos.type === 'BUY';
            const pnl = pos.floating_pnl ?? 0;
            return (
              <span key={pos.mt5_position_id} className="flex items-center gap-1">
                <span className={isBuy ? 'text-green' : 'text-red'}>{pos.type}</span>
                <span className="font-mono">{pos.volume}</span>
                <span className={pnl >= 0 ? 'text-green font-mono' : 'text-red font-mono'}>
                  {formatPnl(pnl, currency)}
                </span>
              </span>
            );
          })}
          {pendingLimitOrders && pendingLimitOrders.length > 0 && (
            <>
              <span className="font-semibold">{pendingLimitOrders.length} pending limit{pendingLimitOrders.length > 1 ? 's' : ''}</span>
              {pendingLimitOrders.map((lo) => (
                <span key={lo.orderId} className="flex items-center gap-1 text-ink-3">
                  <span>{lo.type} LIMIT</span>
                  <span className="font-mono">@ {lo.limitPrice.toFixed(2)}</span>
                </span>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
